import './chartTooltip.less'
import { approximatedValue } from '../../utils'
import { Typography } from '@lightbeamai/design-system'
import { FormattedNumber, useIntl } from 'react-intl'
import { TooltipProps } from 'recharts'
import React, { ReactNode } from 'react'

interface CustomTooltipProps extends TooltipProps<number, string> {
  title?: string
  showAdditionalOnly?: boolean
  formatter?: (val: number) => string
  isApproximated?: boolean
  getTooltipLabelByDataKey?(args: unknown): ReactNode
  getPayloadLabelByDataKey?(key: unknown): ReactNode
}
const CustomTooltip = (props: CustomTooltipProps) => {
  const {
    active,
    payload,
    title,
    showAdditionalOnly = false,
    labelClassName,
    formatter,
    isApproximated = false,
    getTooltipLabelByDataKey,
    getPayloadLabelByDataKey
  } = props

  const intl = useIntl()

  const labelKey = payload?.[0]?.dataKey === 'value' ? `` : 'name'
  const tooltipTitle =
    getTooltipLabelByDataKey?.(payload ?? []) ??
    (payload?.length !== 1 && payload?.[0]?.payload?.name)

  if (active && payload && payload.length) {
    return (
      <div className="colored-custom-tooltip">
        {tooltipTitle && (
          <Typography className="tooltip-title" color="primary" type="medium" variant="baseMinus2">
            {tooltipTitle}
          </Typography>
        )}
        {payload.map((item) => {
          const showTitleWithColor = payload.length === 1 && title
          const label =
            getPayloadLabelByDataKey?.(item.name) ??
            (labelKey ? item[labelKey] : item.payload.name || item.payload.key)
          const color = item.color || item.payload.color
          const percentage = item?.payload?.percentage || item.payload?.payload?.percentage

          return showTitleWithColor ? (
            <div className="tooltip-with-title" key={item.dataKey}>
              <div className="tooltip-title-container">
                <div className="box" style={{ backgroundColor: color }}></div>
                <Typography
                  as="div"
                  color="primary"
                  className={`title-text ${labelClassName || ''}`}
                  type="medium"
                  variant="baseMinus2"
                >
                  {label}
                </Typography>
                {showAdditionalOnly && (
                  <Typography as="div" color="primary" variant="baseMinus2" type="medium">
                    {formatter ? (
                      formatter(item.value || 0)
                    ) : (
                      <FormattedNumber value={item.value || 0} notation="compact">
                        {isApproximated ? approximatedValue : null}
                      </FormattedNumber>
                    )}
                  </Typography>
                )}
              </div>
              {!showAdditionalOnly && (
                <div className="tooltip-text">
                  <Typography as="div" color="secondary" variant="baseMinus2">
                    {title}
                  </Typography>
                  <div className="tooltip-values">
                    <Typography color="primary" variant="baseMinus2" type="medium">
                      {formatter ? (
                        formatter(item.value || 0)
                      ) : (
                        <FormattedNumber value={item.value || 0} notation="compact">
                          {isApproximated ? approximatedValue : null}
                        </FormattedNumber>
                      )}
                    </Typography>
                    {percentage && (
                      <Typography as="div" color="primary" variant="baseMinus2" type="medium">
                        <FormattedNumber
                          value={percentage || 0}
                          notation="compact"
                        ></FormattedNumber>
                        %
                      </Typography>
                    )}
                  </div>
                </div>
              )}
              {item.payload.additionalData &&
                item.payload.additionalData.map((additionalDataItem, index) => {
                  return (
                    <div className="tooltip-text" key={index}>
                      <Typography as="div" color="secondary" variant="baseMinus2">
                        {additionalDataItem.label}
                      </Typography>
                      <Typography as="div" color="primary" variant="baseMinus2" type="medium">
                        {intl.formatNumber(additionalDataItem.value || 0, {
                          notation: 'compact'
                        })}
                      </Typography>
                    </div>
                  )
                })}
            </div>
          ) : (
            <div className="chart-tooltip-item" key={item.dataKey}>
              <div className="box" style={{ backgroundColor: item.color }}></div>
              <div className="tooltip-text">
                <Typography color="secondary" variant="baseMinus2">
                  {label}
                </Typography>
              </div>
              <Typography color="primary" variant="baseMinus2" type="medium">
                <FormattedNumber value={item.value || 0} notation="compact">
                  {isApproximated ? approximatedValue : null}
                </FormattedNumber>
              </Typography>
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

export default CustomTooltip
